import { TONES } from "../../lib/tones.js";

/** The C7 tones a Tabs selection indicator takes (packages/ui/CLAUDE.md). */
export type TabsTone = "neutral" | "ai";

/**
 * @deprecated `teal`, `iris` and `accent` are raw colour names, and a tone is
 * a meaning (C7). `iris` is `ai`; `teal` is the default selection (omit
 * `tone`); `accent` keeps its old accent indicator. Removed in the release
 * after the adoption PR (ADR-0092 amendment).
 */
export type DeprecatedTabsTone = "teal" | "iris" | "accent";

export interface TabsToneClasses {
  /** The horizontal sliding indicator's fill. */
  readonly indicator: string;
  /** The selected vertical tab's end edge. */
  readonly edge: string;
}

/* Colour only; Tabs sets the indicator's size and the edge's width. Literal
   class strings so Tailwind sees them. */
export const TABS_TONES: Readonly<Record<TabsTone, TabsToneClasses>> = {
  // lib/tones.ts: the neutral mark, gated at 3:1 on every surface
  // (tokens.json $contrast.selectionIndicators).
  neutral: { indicator: TONES.neutral.dot, edge: TONES.neutral.dotBorder },
  // The Player AI iris (ADR-0076), in the interactive shade Tabs has always
  // painted: today's `iris` classes, unchanged.
  ai: {
    indicator: "bg-pb-iris-interactive",
    edge: "border-pb-iris-interactive",
  },
};

/* The deprecated names keep today's literal classes, so a call site that has
   not moved yet renders exactly as before. */
const LEGACY_TABS_TONES: Readonly<Record<DeprecatedTabsTone, TabsToneClasses>> =
  {
    teal: { indicator: "bg-pb-teal", edge: "border-pb-teal" },
    iris: TABS_TONES.ai,
    accent: { indicator: "bg-pb-accent", edge: "border-pb-accent" },
  };

/** An omitted tone is the teal selection: teal marks the selected tab (C7,
 *  d3; AP-15, PR 3 moved the default off the accent). */
export function tabsToneClasses(
  tone: TabsTone | DeprecatedTabsTone = "teal",
): TabsToneClasses {
  return tone === "neutral" || tone === "ai"
    ? TABS_TONES[tone]
    : LEGACY_TABS_TONES[tone];
}
